import classNames from "classnames";
import { useEffect, useRef, useState } from "react";

import { Icon } from "src/app/components/icon/Icon";
import { BASE_SYSTEMS, SYSTEM_GROUP } from "src/app/modules/home/constants";

import "./Autocomplete.css";
import autoCSS from "./Autocomplete.module.css";

const systemGroups = SYSTEM_GROUP;
const baseSystems = BASE_SYSTEMS;

const Autocomplete = ({ focusInput, addSystem }: { focusInput: boolean; addSystem: (id: number, shouldAnimate?: boolean) => void }) => {
  const [focused, setFocused] = useState(false);
  const searchGroup = useRef<any>(null);
  const searchInput = useRef<any>(null);
  const [inputValue, setInputValue] = useState("");
  const [filteredSystems, setFilteredSystems] = useState(baseSystems);

  useEffect(() => {
    if (focusInput && searchInput && searchInput.current) {
      setFocused(true);
      searchInput.current.focus();
    }
  }, [focusInput]);

  useEffect(() => {
    const handleClick = (event: MouseEvent) => {
      if (searchGroup.current && !searchGroup.current.contains(event.target as Node) && focused) {
        setFocused(false);
        setInputValue("");
      } else if (searchGroup.current && searchGroup.current.contains(event.target as Node) && !focused) {
        setFocused(true);
      }
    };
    document.addEventListener("mousedown", handleClick);
  }, [searchGroup, focused]);

  useEffect(() => {
    let filtered = baseSystems;

    if (inputValue) {
      filtered = baseSystems.filter((system) => system.name.toLowerCase().includes(inputValue.toLowerCase()));
    }
    setFilteredSystems(filtered);
  }, [inputValue]);

  const handleUserInput: any = (event: any) => {
    setInputValue(event.target.value);
  };

  const resetInputField = () => {
    setInputValue("");
    searchInput.current.focus();
  };

  const handleAddSystem = (system: any, shouldAnimate?: boolean) => {
    addSystem(system, shouldAnimate);
    setFocused(false);
  };

  return (
    <div ref={searchGroup} className={classNames("u-flex", "u-flex-content-between", "u-flex-items-center", autoCSS.searchGroupNew)}>
      <button
        type="button"
        className={classNames("u-z-index1", "u-t-black", "u-t-f15p", "u-p0", "u-pos-absolute", "u-cursor-pointer", autoCSS.searchButtonNew)}
      >
        <Icon name="search" className="u-t-valign-mid" />
      </button>
      <input type="text" value={inputValue} onChange={handleUserInput} placeholder="Pronađi sustav" ref={searchInput} />
      <div className={classNames(autoCSS.suggestionContainer, focused ? "" : "u-none")}>
        {/* System Groups and Systems */}
        {Object.keys(systemGroups)
          .filter((k) => filteredSystems.filter((sys) => sys.sysGroupId === systemGroups[k].id).length > 0)
          .map((k, i) => (
            <SystemGroup
              key={k}
              systemGroup={systemGroups[k]}
              systems={filteredSystems.filter((sys) => sys.sysGroupId === systemGroups[k].id)}
              addSystem={handleAddSystem}
              match={inputValue}
              index={i}
            />
          ))}
        {/* No results */}
        {filteredSystems.length === 0 && (
          <div className={autoCSS.noResultsWrap}>
            <div className={classNames("dropdown-button", autoCSS.noResults)}>
              <span className={classNames("u-t-w400", "u-t-secondary", autoCSS.dropdownText)}>Nema rezultata</span>
            </div>
          </div>
        )}
      </div>
      <button
        type="button"
        onClick={resetInputField}
        className={classNames("u-z-index1", "u-t-secondary", "u-t-f10p", "u-p0", "u-none", "u-cursor-pointer", autoCSS.clearButtonNew, focused ? "" : "u-none")}
      >
        <Icon name="times" className="u-t-valign-mid" />
      </button>
    </div>
  );
};

export default Autocomplete;

const SystemGroup = ({
  systemGroup,
  systems,
  addSystem,
  match,
  index,
}: {
  systemGroup: any;
  systems: any[];
  addSystem: (system: any, shouldAnimate?: boolean) => void;
  match: string;
  index: number;
}) => {
  const [isActive, setIsActive] = useState(false);

  useEffect(() => {
    setIsActive(match.length > 0);
  }, [match]);

  const handleAddSystem = (system: any, shouldAnimate?: boolean) => {
    addSystem(system, shouldAnimate);
    setIsActive(false);
  };

  return (
    <div className={autoCSS.systemDropdown} style={{ zIndex: 100 - index }}>
      <div className={classNames("u-w100", "dropdown-button", "u-cursor-pointer", autoCSS.dropdownButton)} onClick={() => setIsActive(!isActive)}>
        <span className={classNames("u-t-w700", autoCSS.dropdownText, isActive ? "u-t-primary-dark" : "u-t-black")}>{systemGroup.value}</span>
        <Icon name={isActive ? "arrow-up" : "arrow-down"} className="u-t-f24p u-t-secondary u-pos-absolute u-right20px" />
      </div>
      <div className={classNames(autoCSS.dropdownItemsWrap, isActive ? autoCSS.activeDropdown : autoCSS.inactiveDropdown)}>
        {systems && systems.map((sys) => <System system={sys} addSystem={handleAddSystem} match={match} />)}
      </div>
    </div>
  );
};

const System = ({ system, addSystem, match }: { system: any; addSystem: (id: any, shouldAnimate?: boolean) => void; match: string }) => {
  const [tooltipVisible, setTooltipVisible] = useState(false);
  return (
    <div className={classNames("dropdown-button", "u-cursor-pointer", autoCSS.dropdownItem)}>
      {!match ? (
        <span onClick={() => addSystem(system, true)} className={autoCSS.dropdownText}>
          {system.name}
        </span>
      ) : (
        <span onClick={() => addSystem(system, true)} className={autoCSS.dropdownText}>
          <strong>{system.name.slice(0, match.length)}</strong>
          {system.name.slice(match.length)}
        </span>
      )}
      <div className="u-flex u-flex-items-center">
        <div
          className="u-flex u-flex-items-center u-mr20px"
          onMouseEnter={() => {
            setTooltipVisible(true);
          }}
          onMouseLeave={() => {
            setTooltipVisible(false);
          }}
        >
          <Icon name={"help-circle"} className={classNames(autoCSS.helpButton)} />
        </div>

        {tooltipVisible && (
          <div className={classNames(autoCSS.tooltipContainer)}>
            <h6 className="u-m0 u-t-w400">{system.tooltip}</h6>
          </div>
        )}
        <div onClick={() => addSystem(system, true)} className="u-t-f12p u-t-primary">
          <Icon name={"plus"} />
        </div>
      </div>
    </div>
  );
};
